import React, { useState, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import SurveyDetailModal from "../components/SurveyDetailModal";
import { formatName } from "../../../../helpers/helpers";

export default function AllSurveysTabDisplay(props) {
  const { surveys, dateRange } = props;

  // State for filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [staffFilter, setStaffFilter] = useState("all");

  // State for sorting
  const [orderBy, setOrderBy] = useState("date");
  const [order, setOrder] = useState("desc");

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  // Get unique staff members for filter
  const uniqueStaff = useMemo(() => {
    const staffMap = new Map();
    surveys.forEach((survey) => {
      survey.staff_members.forEach((staff) => {
        if (!staffMap.has(staff.id)) {
          staffMap.set(staff.id, staff.name);
        }
      });
    });
    return Array.from(staffMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [surveys]);

  // Filter surveys
  const filteredSurveys = useMemo(() => {
    return surveys.filter((survey) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        survey.feedback?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        survey.staff_members.some((s) =>
          s.name.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        (survey.order_items &&
          survey.order_items.some((item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
          ));

      // Rating filter
      const matchesRating =
        ratingFilter === "all" ||
        (ratingFilter === "5" && survey.rating === 5) ||
        (ratingFilter === "4" && survey.rating === 4) ||
        (ratingFilter === "3" && survey.rating === 3) ||
        (ratingFilter === "2" && survey.rating === 2) ||
        (ratingFilter === "1" && survey.rating === 1) ||
        (ratingFilter === "positive" && survey.rating === 5) ||
        (ratingFilter === "negative" && survey.rating <= 3);

      // Source filter
      const matchesSource =
        sourceFilter === "all" || survey.source === sourceFilter;

      // Staff filter
      const matchesStaff =
        staffFilter === "all" ||
        survey.staff_members.some((s) => s.id === staffFilter);

      return matchesSearch && matchesRating && matchesSource && matchesStaff;
    });
  }, [surveys, searchTerm, ratingFilter, sourceFilter, staffFilter]);

  // Sort surveys
  const sortedSurveys = useMemo(() => {
    const sorted = [...filteredSurveys].sort((a, b) => {
      let comparison = 0;

      switch (orderBy) {
        case "date":
          // Compare date strings first, then decimal_hour
          if (a.date === b.date) {
            comparison = a.decimal_hour - b.decimal_hour;
          } else {
            comparison = a.date.localeCompare(b.date);
          }
          break;
        case "rating":
          comparison = a.rating - b.rating;
          break;
        case "order_total":
          const aTotal = parseFloat(a.order_total || 0);
          const bTotal = parseFloat(b.order_total || 0);
          comparison = aTotal - bTotal;
          break;
        case "source":
          comparison = a.source.localeCompare(b.source);
          break;
        default:
          return 0;
      }

      return order === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [filteredSurveys, orderBy, order]);

  // Helper functions
  const formatDateShort = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}`;
  };

  const formatTime = (decimalHour) => {
    const hour = Math.floor(decimalHour);
    const minute = Math.floor((decimalHour - hour) * 60);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const getRatingColor = (rating) => {
    if (rating === 5)
      return { bg: "bg-green-500", text: "text-green-600", borderColor: "#22c55e" };
    if (rating === 4)
      return { bg: "bg-lime-500", text: "text-lime-600", borderColor: "#84cc16" };
    if (rating === 3)
      return { bg: "bg-yellow-500", text: "text-yellow-600", borderColor: "#eab308" };
    if (rating === 2)
      return { bg: "bg-orange-500", text: "text-orange-600", borderColor: "#f97316" };
    return { bg: "bg-red-500", text: "text-red-600", borderColor: "#ef4444" };
  };

  const getSourceBadge = (source) => {
    if (source === "toast api")
      return { bg: "bg-blue-100", text: "text-blue-800" };
    if (source === "dine in")
      return { bg: "bg-purple-100", text: "text-purple-800" };
    if (source === "NFC") return { bg: "bg-green-100", text: "text-green-800" };
    return { bg: "bg-gray-100", text: "text-gray-800" };
  };

  const getPrimaryStaff = (staffMembers) => {
    const primary = staffMembers.find((s) => s.is_primary);
    if (primary) return primary;
    return staffMembers[0] || null;
  };

  // Handlers
  const handleSort = useCallback((column) => {
    setOrderBy((prevOrderBy) => {
      if (prevOrderBy === column) {
        setOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
      } else {
        setOrder("desc");
      }
      return column;
    });
  }, []);

  const handleRowClick = useCallback((survey) => {
    setSelectedSurvey(survey);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedSurvey(null);
  }, []);

  return (
    <div className="space-y-6">
      {/* Filters & Search Bar */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <div className="grid grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Search
            </label>
            <TextField
              fullWidth
              placeholder="Search feedback, staff, items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="text-gray-400" />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: "white",
                  borderRadius: "10px",
                  height: "42px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
          </div>

          {/* Rating Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Rating
            </label>
            <Select
              fullWidth
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                height: "42px",
                ".MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1px",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
            >
              <MenuItem value="all">All Ratings</MenuItem>
              <MenuItem value="positive">Positive (5 stars)</MenuItem>
              <MenuItem value="negative">Negative (1-3 stars)</MenuItem>
              <MenuItem value="5">Excellent (5★)</MenuItem>
              <MenuItem value="4">Good (4★)</MenuItem>
              <MenuItem value="3">Average (3★)</MenuItem>
              <MenuItem value="2">Poor (2★)</MenuItem>
              <MenuItem value="1">Very Poor (1★)</MenuItem>
            </Select>
          </div>

          {/* Source Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Source
            </label>
            <Select
              fullWidth
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                height: "42px",
                ".MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1px",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
            >
              <MenuItem value="all">All Sources</MenuItem>
              <MenuItem value="toast api">Toast API (exact)</MenuItem>
              <MenuItem value="dine in">Dine In (approx)</MenuItem>
              <MenuItem value="NFC">NFC (approx)</MenuItem>
            </Select>
          </div>

          {/* Staff Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Staff Member
            </label>
            <Select
              fullWidth
              value={staffFilter}
              onChange={(e) => setStaffFilter(e.target.value)}
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                height: "42px",
                ".MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1px",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
            >
              <MenuItem value="all">All Staff</MenuItem>
              {uniqueStaff.map((staff) => (
                <MenuItem key={staff.id} value={staff.id}>
                  {formatName(staff.name)}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mt-4">
          <p className="text-sm text-gray-600">
            Showing {sortedSurveys.length} surveys
          </p>
        </div>
      </div>

      {/* Results Table */}
      {sortedSurveys.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow border border-gray-200 text-center">
          <p className="text-lg text-slate-500">
            {surveys.length > 0
              ? "No surveys match your filters. Try adjusting your search criteria."
              : "No surveys found for this date range."}
          </p>
        </div>
      ) : (
        <TableContainer component={Paper} className="shadow border border-gray-200">
          <Table>
            <TableHead className="bg-slate-50">
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>
                  <TableSortLabel
                    active={orderBy === "date"}
                    direction={orderBy === "date" ? order : "asc"}
                    onClick={() => handleSort("date")}
                  >
                    Date & Time
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>
                  <TableSortLabel
                    active={orderBy === "rating"}
                    direction={orderBy === "rating" ? order : "asc"}
                    onClick={() => handleSort("rating")}
                  >
                    Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>
                  Feedback
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>
                  Staff
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>
                  <TableSortLabel
                    active={orderBy === "order_total"}
                    direction={orderBy === "order_total" ? order : "asc"}
                    onClick={() => handleSort("order_total")}
                  >
                    Order Total
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>
                  <TableSortLabel
                    active={orderBy === "source"}
                    direction={orderBy === "source" ? order : "asc"}
                    onClick={() => handleSort("source")}
                  >
                    Source
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSurveys.map((survey, index) => {
                const ratingColors = getRatingColor(survey.rating);
                const sourceBadge = getSourceBadge(survey.source);
                const primaryStaff = getPrimaryStaff(survey.staff_members);

                return (
                  <TableRow
                    key={survey.id || index}
                    hover
                    onClick={() => handleRowClick(survey)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#f8fafc" },
                    }}
                  >
                    {/* Date & Time */}
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium text-slate-900">
                          {formatDateShort(survey.date)}
                        </div>
                        <div className="text-xs text-slate-600">
                          {formatTime(survey.decimal_hour)}
                        </div>
                      </div>
                    </TableCell>

                    {/* Rating */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span
                          className={`${ratingColors.bg} text-white px-2.5 py-1 rounded-md font-bold text-sm`}
                        >
                          {survey.rating}
                        </span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <StarIcon
                              key={i}
                              className={`${
                                i < survey.rating
                                  ? "text-yellow-400"
                                  : "text-gray-300"
                              }`}
                              sx={{ fontSize: "16px" }}
                            />
                          ))}
                        </div>
                      </div>
                    </TableCell>

                    {/* Feedback */}
                    <TableCell>
                      {survey.feedback ? (
                        <p className="text-sm text-slate-600 italic">
                          {survey.feedback.length > 80
                            ? `"${survey.feedback.substring(0, 80)}..."`
                            : `"${survey.feedback}"`}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400 italic">
                          No feedback provided
                        </p>
                      )}
                    </TableCell>

                    {/* Staff */}
                    <TableCell>
                      {primaryStaff ? (
                        <div className="text-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">
                              {formatName(primaryStaff.name)}
                            </span>
                            {primaryStaff.is_primary && (
                              <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-md font-medium">
                                Primary
                              </span>
                            )}
                          </div>
                          {survey.staff_members.length > 1 && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              +{survey.staff_members.length - 1} more
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 italic">No staff data</p>
                      )}
                    </TableCell>

                    {/* Order Total */}
                    <TableCell>
                      {survey.source === "toast api" && survey.order_total ? (
                        <span className="text-sm font-medium text-slate-900">
                          ${parseFloat(survey.order_total).toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </TableCell>

                    {/* Source */}
                    <TableCell>
                      <div>
                        <span
                          className={`${sourceBadge.bg} ${sourceBadge.text} px-2 py-1 rounded text-xs font-medium`}
                        >
                          {survey.source}
                        </span>
                        <div className="text-xs text-slate-500 mt-1">
                          {survey.timing_type}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Survey Detail Modal */}
      <SurveyDetailModal
        open={openModal}
        onClose={handleCloseModal}
        survey={selectedSurvey}
      />
    </div>
  );
}
